import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, ChevronDown, Mail, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const ChatbotWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [settings, setSettings] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  // Fetch settings and FAQs on mount
  useEffect(() => {
    fetchSettings();
    fetchFaqs();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Add welcome message when opened
  useEffect(() => {
    if (isOpen && messages.length === 0 && settings) {
      setMessages([{
        type: 'bot',
        text: settings.welcome_message || 'Hallo! 👋 Wie kann ich dir helfen?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, settings]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chatbot/settings`);
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      console.error('Failed to fetch chatbot settings:', err);
      setSettings({
        welcome_message: 'Hallo! 👋 Wie kann ich dir helfen?',
        fallback_message: 'Das kann ich leider nicht beantworten. Schreib uns gerne eine E-Mail an info@revocut-media.de',
        email_prompt: 'Für weitere Fragen erreichst du uns unter info@revocut-media.de',
        is_active: true
      });
    }
  };

  const fetchFaqs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/faq`);
      if (!res.ok || !res.headers.get('content-type')?.includes('application/json')) return;
      const data = await res.json();
      setFaqs(data);
    } catch (err) {
      console.error('Failed to fetch FAQs:', err);
    }
  };

  const searchFaqs = async (query) => {
    try {
      const res = await fetch(`${API_URL}/api/faq/search?q=${encodeURIComponent(query)}`);
      if (!res.ok || !res.headers.get('content-type')?.includes('application/json')) return [];
      return await res.json();
    } catch (err) {
      console.error('Failed to search FAQs:', err);
      return [];
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setShowSuggestions(false);

    // Add user message
    const newUserMsg = {
      type: 'user',
      text: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMsg]);

    // Show typing indicator
    setIsTyping(true);

    // First, try FAQ search
    const results = await searchFaqs(userMessage);

    if (results.length > 0 && results[0].score >= 5) {
      // Good FAQ match found
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsTyping(false);
      
      const bestMatch = results[0];
      setMessages(prev => [...prev, {
        type: 'bot',
        text: bestMatch.answer,
        timestamp: new Date()
      }]);

      // If there are more matches, offer them
      if (results.length > 1) {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'bot',
            text: 'Vielleicht hilft dir auch eine dieser Fragen:',
            suggestions: results.slice(1, 4).map(r => r.question),
            timestamp: new Date()
          }]);
        }, 500);
      }
    } else {
      // No good FAQ match - use GPT
      try {
        const response = await fetch(`${API_URL}/api/chatbot/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: userMessage,
            history: messages.slice(-6)
          })
        });
        
        if (!response.ok || !response.headers.get('content-type')?.includes('application/json')) {
          throw new Error('Invalid response from chat API');
        }
        
        const data = await response.json();
        setIsTyping(false);
        
        setMessages(prev => [...prev, {
          type: 'bot',
          text: data.response,
          showEmailButton: data.is_fallback,
          timestamp: new Date()
        }]);
      } catch (err) {
        console.error('GPT chat error:', err);
        setIsTyping(false);
        setMessages(prev => [...prev, {
          type: 'bot',
          text: settings?.fallback_message || 'Das kann ich leider nicht beantworten. Schreib uns gerne eine E-Mail an info@revocut-media.de',
          showEmailButton: true,
          timestamp: new Date()
        }]);
      }
    }
  };

  const handleSuggestionClick = (question) => {
    setInputValue(question);
    // Auto-send
    setTimeout(() => {
      const input = document.getElementById('chatbot-input');
      if (input) {
        input.value = question;
        handleSendWithQuestion(question);
      }
    }, 100);
  };

  const handleSendWithQuestion = async (question) => {
    setInputValue('');
    setShowSuggestions(false);

    // Add user message
    const newUserMsg = {
      type: 'user',
      text: question,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newUserMsg]);

    // Show typing indicator
    setIsTyping(true);

    // Search for matching FAQs
    const results = await searchFaqs(question);

    if (results.length > 0 && results[0].score >= 5) {
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsTyping(false);
      
      const bestMatch = results[0];
      setMessages(prev => [...prev, {
        type: 'bot',
        text: bestMatch.answer,
        timestamp: new Date()
      }]);
    } else {
      // Use GPT
      try {
        const response = await fetch(`${API_URL}/api/chatbot/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: question,
            history: messages.slice(-6)
          })
        });
        
        const data = await response.json();
        setIsTyping(false);
        
        setMessages(prev => [...prev, {
          type: 'bot',
          text: data.response,
          showEmailButton: data.is_fallback,
          timestamp: new Date()
        }]);
      } catch (err) {
        setIsTyping(false);
        setMessages(prev => [...prev, {
          type: 'bot',
          text: settings?.fallback_message || 'Das kann ich leider nicht beantworten.',
          showEmailButton: true,
          timestamp: new Date()
        }]);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Get suggested questions (first 3 FAQs)
  const suggestedQuestions = faqs.slice(0, 4).map(f => f.question);

  if (!settings?.is_active) return null;

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'scale-0' : 'scale-100 hover:scale-110'
        }`}
        style={{ backgroundColor: '#5C63FF', zIndex: 99999 }}
        data-testid="chatbot-toggle"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        {/* Notification dot */}
        <span className="absolute top-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
      </button>

      {/* Chat Window */}
      <div
        className={`fixed bottom-6 right-6 w-96 max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'
        }`}
        style={{ 
          backgroundColor: '#1a1a2e',
          border: '1px solid rgba(255,255,255,0.1)',
          maxHeight: 'calc(100vh - 6rem)',
          zIndex: 99999
        }}
        data-testid="chatbot-window"
      >
        {/* Header */}
        <div 
          className="px-4 py-3 flex items-center justify-between"
          style={{ backgroundColor: '#5C63FF' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">RevoCut Support</h3>
              <p className="text-xs text-white/70">Wir antworten sofort</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Messages */}
        <div 
          className="h-80 overflow-y-auto p-4 space-y-4"
          style={{ backgroundColor: '#0f0f1a' }}
        >
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  msg.type === 'user'
                    ? 'bg-[#5C63FF] text-white rounded-br-none'
                    : 'bg-white/10 text-white rounded-bl-none'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                
                {/* Suggestions */}
                {msg.suggestions && (
                  <div className="mt-2 space-y-1">
                    {msg.suggestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleSuggestionClick(q)}
                        className="block w-full text-left text-xs px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-gray-300"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}

                {/* Email button */}
                {msg.showEmailButton && (
                  <a
                    href="mailto:info@revocut-media.de"
                    className="mt-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                  >
                    <Mail className="w-3 h-3" />
                    E-Mail schreiben
                  </a>
                )}
              </div>
            </div>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white/10 rounded-2xl rounded-bl-none px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions */}
        {showSuggestions && suggestedQuestions.length > 0 && messages.length <= 1 && (
          <div className="px-4 py-2 border-t border-white/5" style={{ backgroundColor: '#0f0f1a' }}>
            <p className="text-xs text-gray-500 mb-2">Häufige Fragen:</p>
            <div className="flex flex-wrap gap-1">
              {suggestedQuestions.map((q, i) => (
                <button
                  key={i}
                  onClick={() => handleSuggestionClick(q)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-gray-300 transition-colors truncate max-w-full"
                >
                  {q.length > 40 ? q.substring(0, 40) + '...' : q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div 
          className="p-3 border-t border-white/10"
          style={{ backgroundColor: '#1a1a2e' }}
        >
          <div className="flex items-center gap-2">
            <input
              id="chatbot-input"
              data-testid="chatbot-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Schreib eine Nachricht..."
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#5C63FF]"
            />
            <button
              onClick={handleSend}
              disabled={!inputValue.trim()}
              data-testid="chatbot-send"
              className="w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: '#5C63FF' }}
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatbotWidget;
